import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input, output } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { polarToCartesian } from '../mandala-geometry.utils';
import { gateStartLongitude, longitudeToScreenAngle } from '../mandala-layout';
import { MANDALA_DEGREES_PER_GATE } from '../mandala.constants';
import { HdLocksKeysPeriodId } from './hd-locks-keys-metadata';
import {
  HD_GLOBAL_CYCLES_RANGE,
  HD_GLOBAL_CYCLES_ROWS,
  HD_GLOBAL_CYCLES_TITLE,
  HdGlobalCyclesRow,
} from './hd-global-cycles-table.data';

/** Maten van het wieltje per rij (viewBox, px). */
const DIAL_SIZE = 20;
const DIAL_CENTER = DIAL_SIZE / 2;
const DIAL_RING_RADIUS = 9;
const DIAL_SQUARE_RADIUS = 6.5;
const DIAL_HAND_RADIUS = 8.6;
const DIAL_LOCK_TICK_INNER = 7.2;
const DIAL_LOCK_TICK_OUTER = 9.6;
const LOCK_GATES: readonly number[] = [13, 25, 2, 15, 7, 46, 1, 10];

/**
 * Een viertal staat in kruisnotatie [x, x+180°, x−90°, x+90°]; rond het wiel
 * gelezen is dat x, x+90°, x+180°, x−90°. Geldt voor beide kanten (rechts is
 * hetzelfde vierkant, 45° verschoven).
 */
const SQUARE_CORNER_ORDER: readonly number[] = [0, 3, 1, 2];

/** Hoek van elke kolom ten opzichte van het lentepunt (kolom 1 links), voor de kolomkoppen. */
const LEFT_ANGLES = [
  { label: '0°', key: 'angle0' },
  { label: '180°', key: 'angle180' },
  { label: '−90°', key: 'angleMinus90' },
  { label: '+90°', key: 'anglePlus90' },
] as const;
const RIGHT_ANGLES = [
  { label: '−45°', key: 'angleMinus45' },
  { label: '+135°', key: 'anglePlus135' },
  { label: '−135°', key: 'angleMinus135' },
  { label: '+45°', key: 'anglePlus45' },
] as const;

interface DialVm {
  readonly hand: { readonly x: number; readonly y: number };
  readonly equinoxSquare: string;
  readonly counterSquare: string;
  readonly title: string;
}

interface DialTickVm {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

/**
 * Rij met de tooltips van de markeringen: het variantnummer achter elke
 * kruisnaam, op een lock-rij vanaf blok 2 de doorgeef-pijl (links) en de
 * kwartslag (rechts), en het wieltje met de wijzer van het lentepunt.
 */
interface GlobalCyclesRowVm {
  readonly row: HdGlobalCyclesRow;
  readonly leftVariantTitle: string;
  readonly rightVariantTitle: string;
  /** Enkel op een lock-rij vanaf blok 2; anders null. */
  readonly handoverTitle: string | null;
  readonly turnTitle: string | null;
  readonly equinoxTitle: string;
  readonly dial: DialVm;
}

interface GlobalCyclesHeadVm {
  readonly era: string;
  readonly phase: string;
  readonly wheel: string;
  readonly wheelTitle: string;
  readonly equinoxPoint: string;
  readonly equinoxSquare: string;
  readonly counterSquare: string;
  readonly cross: string;
  readonly left: readonly { readonly label: string; readonly title: string }[];
  readonly right: readonly { readonly label: string; readonly title: string }[];
}

/**
 * De volledige Global Cycles-tabel (16513 BC – 3674 AD), zelf gerenderd naar
 * het referentiebeeld uit het bronmateriaal: zelfde blokkleuren, rijindeling
 * en de wit uitgelichte 1615-2026-rij, zodat de tabel direct herkenbaar is.
 * Vaste ontwerpbreedte ({@link scss}: 960px) — consumenten schalen hem zelf
 * (de intro-preview via transform, het zwevende paneel via scroll).
 *
 * Bovenop het bronbeeld: het variantnummer van elk kruis (kwartaal van de
 * poort in kolom 1) en op elke lock-rij vanaf blok 2 een pijl voor de kant die
 * ongewijzigd doorgegeven wordt en een kwartslag-icoon voor de kant die draait.
 * Kolom 1 is rood omrand: het lentepunt, de enige kolom die zelf door het wiel
 * reist. De kolomkoppen geven de vaste hoek van elke andere key ten opzichte
 * ervan, en het wieltje vooraan elke rij toont de wijzer tussen de vaste locks.
 */
@Component({
  selector: 'hd-global-cycles-table',
  imports: [SharedModule],
  templateUrl: './hd-global-cycles-table.component.html',
  styleUrl: './hd-global-cycles-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdGlobalCyclesTableComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Rij van deze app-periode krijgt een dusk-blauwe selectierand; null = geen. */
  readonly highlightPeriodId = input<HdLocksKeysPeriodId | null>(null);

  /** Rijen klikbaar maken (zwevend tabelpaneel); de preview op de infopagina blijft passief. */
  readonly selectable = input(false, { transform: booleanAttribute });

  /**
   * Kolomkoppen laten kleven in de scrollcontainer van de consument, op
   * `--gc-table-head-top` (het zwevende paneel zet daar de vlechthoogte).
   */
  readonly stickyHead = input(false, { transform: booleanAttribute });

  /** Klik op een rij: elke rij is een Locks & Keys-periode. */
  readonly periodSelected = output<HdLocksKeysPeriodId>();

  protected select(periodId: HdLocksKeysPeriodId): void {
    if (this.selectable()) {
      this.periodSelected.emit(periodId);
    }
  }

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  private t(key: string, params?: Record<string, unknown>): string {
    return this.gateInfo.locksKeysTextIn(`globalCycles.${key}`, this.lang(), params);
  }

  private variantTitle(gate: number, variant: number): string {
    return this.t('variantTooltip', { variant, gate, quarter: this.t(`quarter${variant}`) });
  }

  protected readonly head = computed<GlobalCyclesHeadVm>(() => ({
    era: this.t('headEra'),
    phase: this.t('headPhase'),
    wheel: this.t('headWheel'),
    wheelTitle: this.t('headWheelTooltip'),
    equinoxPoint: this.t('headEquinoxPoint'),
    equinoxSquare: this.t('headEquinoxSquare'),
    counterSquare: this.t('headCounterSquare'),
    cross: this.t('headCross'),
    left: LEFT_ANGLES.map(angle => ({ label: angle.label, title: this.t(angle.key) })),
    right: RIGHT_ANGLES.map(angle => ({ label: angle.label, title: this.t(angle.key) })),
  }));

  protected readonly rowVms = computed<readonly GlobalCyclesRowVm[]>(() => {
    let lockGate = HD_GLOBAL_CYCLES_ROWS[0].leftGates[0];
    return HD_GLOBAL_CYCLES_ROWS.map(row => {
      const equinoxGate = row.leftGates[0];
      if (row.kind === 'lock') {
        lockGate = equinoxGate;
      }
      return {
        row,
        leftVariantTitle: this.variantTitle(equinoxGate, row.leftVariant),
        rightVariantTitle: this.variantTitle(row.rightGates[0], row.rightVariant),
        handoverTitle: row.rightTurnedFrom == null ? null : this.t('handoverTooltip', { gate: equinoxGate }),
        turnTitle: row.rightTurnedFrom == null
          ? null
          : this.t('turnTooltip', { from: row.rightTurnedFrom.join('·'), to: row.rightGates.join('·') }),
        equinoxTitle: this.t('equinoxCellTooltip', { gate: equinoxGate }),
        dial: {
          hand: dialPoint(equinoxGate, DIAL_HAND_RADIUS),
          equinoxSquare: dialSquare(row.leftGates),
          counterSquare: dialSquare(row.rightGates),
          title: row.kind === 'lock'
            ? this.t('dialLockTooltip', { gate: equinoxGate })
            : this.t(row.keyShift === 1 ? 'dialKeyTooltipOne' : 'dialKeyTooltip', {
                gate: equinoxGate,
                shift: row.keyShift,
                lock: lockGate,
              }),
        },
      };
    });
  });

  protected readonly dialSize = DIAL_SIZE;
  protected readonly dialCenter = DIAL_CENTER;
  protected readonly dialRingRadius = DIAL_RING_RADIUS;

  /** De acht locks staan vast: dezelfde streepjes in elk wieltje. */
  protected readonly dialLockTicks: readonly DialTickVm[] = LOCK_GATES.map(gate => {
    const inner = dialPoint(gate, DIAL_LOCK_TICK_INNER);
    const outer = dialPoint(gate, DIAL_LOCK_TICK_OUTER);
    return { x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y };
  });

  protected readonly title = HD_GLOBAL_CYCLES_TITLE;
  protected readonly range = HD_GLOBAL_CYCLES_RANGE;
}

/** Punt op het wieltje bij het midden van een poort, in dezelfde oriëntatie als het mandala-wiel. */
function dialPoint(gate: number, radius: number): { x: number; y: number } {
  const angle = longitudeToScreenAngle(gateStartLongitude(gate) + MANDALA_DEGREES_PER_GATE / 2);
  const point = polarToCartesian(DIAL_CENTER, DIAL_CENTER, radius, angle);
  return { x: round(point.x), y: round(point.y) };
}

function dialSquare(gates: readonly number[]): string {
  return SQUARE_CORNER_ORDER
    .map(index => dialPoint(gates[index], DIAL_SQUARE_RADIUS))
    .map(point => `${point.x},${point.y}`)
    .join(' ');
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}
